// The credential of a password recovery link (CHT-1628 review, DEBT-01). A recovery link IS the credential in Supabase's own flow, so the
// app accepts it, but it never trusts a link it did not ask for in a way that could switch the signed-in person to someone else's account
// (login CSRF, session fixation). The rules, in one place and pure:
//   - the tokens of a link count only on the dedicated callback route (`auth/callback`) and only with `type=recovery`;
//   - the access token must look like what a recovery link makes: a subject, issued in the last 15 minutes, with a one-time-link method
//     (`otp`, or `recovery`) in its `amr` claim. The signature is NOT checked here: the server confirms the token (`getUser`) next;
//   - the form shows whose account it is (a masked e-mail), and an existing session is replaced only when the person submits the form.
// The same file exists for the mobile app (`apps/mobile/src/auth/recovery-credential.ts`). Keep them equal, except `isCallbackRoute`: on the
// admin site the page `/auth/callback` is the route, so the link is read from `window.location` on that page only.

export const RECOVERY_TOKEN_MAX_AGE_SECONDS = 15 * 60;
/** A token issued a little in the future (clock skew between the server and this device) is still fresh. */
const CLOCK_SKEW_SECONDS = 60;
/** The `amr` methods that a one-time recovery link produces. A password, a refresh or an OAuth sign-in has none of them. */
const ONE_TIME_LINK_METHODS: ReadonlySet<string> = new Set(["otp", "recovery"]);

const BASE64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** The JSON payload of a JWT, decoded without any platform API (Hermes has no `Buffer`, and `atob` is not on every version). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (part === undefined || part === "" || token.split(".").length !== 3) return null;
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const char of part.replace(/=+$/, "")) {
    const index = BASE64URL.indexOf(char);
    if (index === -1) return null;
    value = (value << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((value >> bits) & 0xff);
    }
  }
  try {
    const text = decodeURIComponent(
      bytes.map((byte) => `%${byte.toString(16).padStart(2, "0")}`).join(""),
    );
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export type RecoveryTokenRefusal =
  | "MALFORMED"
  | "NO_SUBJECT"
  | "NO_ISSUED_AT"
  | "STALE"
  | "FROM_THE_FUTURE"
  | "NOT_A_RECOVERY_LINK";

export type RecoveryTokenCheck =
  | Readonly<{ ok: true; subject: string }>
  | Readonly<{ ok: false; reason: RecoveryTokenRefusal }>;

function methodsOf(amr: unknown): string[] {
  if (!Array.isArray(amr)) return [];
  return amr.flatMap((entry) => {
    if (typeof entry === "string") return [entry];
    if (
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as { method?: unknown }).method === "string"
    ) {
      return [(entry as { method: string }).method];
    }
    return [];
  });
}

/**
 * Whether an access token looks like the one that a recovery link makes. Local checks only (no signature): the subject is there, the token
 * was issued within `RECOVERY_TOKEN_MAX_AGE_SECONDS`, and `amr` shows a one-time-link method. `nowSeconds` is the clock of this device.
 */
export function checkRecoveryAccessToken(
  accessToken: string,
  nowSeconds: number,
): RecoveryTokenCheck {
  const claims = decodeJwtPayload(accessToken);
  if (claims === null) return { ok: false, reason: "MALFORMED" };
  if (typeof claims.sub !== "string" || claims.sub === "")
    return { ok: false, reason: "NO_SUBJECT" };
  if (typeof claims.iat !== "number" || !Number.isFinite(claims.iat))
    return { ok: false, reason: "NO_ISSUED_AT" };
  if (claims.iat - nowSeconds > CLOCK_SKEW_SECONDS) return { ok: false, reason: "FROM_THE_FUTURE" };
  if (nowSeconds - claims.iat > RECOVERY_TOKEN_MAX_AGE_SECONDS)
    return { ok: false, reason: "STALE" };
  if (!methodsOf(claims.amr).some((method) => ONE_TIME_LINK_METHODS.has(method))) {
    return { ok: false, reason: "NOT_A_RECOVERY_LINK" };
  }
  return { ok: true, subject: claims.sub };
}

/** `j***@example.com`. Enough to tell whose account it is; not enough to learn the address. */
export function maskEmail(email: string | null | undefined): string {
  if (typeof email !== "string") return "***";
  const at = email.lastIndexOf("@");
  if (at < 1) return "***";
  const first = [...email.slice(0, at)][0] ?? "";
  return `${first}***${email.slice(at)}`;
}

export type RecoveryLink =
  | Readonly<{ kind: "none" }>
  | Readonly<{ kind: "tokens"; accessToken: string; refreshToken: string }>
  | Readonly<{ kind: "token_hash"; tokenHash: string }>;

/** The admin page `/auth/callback` is the only reader of a link, so the route is the page. A URL of another path is not a callback. */
export function isCallbackRoute(url: string): boolean {
  try {
    return new URL(url).pathname.replace(/\/+$/g, "") === "/auth/callback";
  } catch {
    return false;
  }
}

/**
 * The recovery credential in a link, or `none`. Only the callback route counts and only `type=recovery`: fragment tokens or a `token_hash`
 * in any other link (a trip, a chat, a push) are ignored, whatever else they say. The `token_hash` form may sit in the query or in the
 * fragment; the fragment tokens only in the fragment.
 */
export function parseRecoveryLink(url: string): RecoveryLink {
  if (!isCallbackRoute(url)) return { kind: "none" };
  try {
    const parsed = new URL(url);
    const fragment = new URLSearchParams(parsed.hash.replace(/^#/, ""));
    const accessToken = fragment.get("access_token");
    const refreshToken = fragment.get("refresh_token");
    if (fragment.get("type") === "recovery" && accessToken && refreshToken) {
      return { kind: "tokens", accessToken, refreshToken };
    }
    for (const source of [parsed.searchParams, fragment]) {
      const tokenHash = source.get("token_hash");
      if (source.get("type") === "recovery" && tokenHash) return { kind: "token_hash", tokenHash };
    }
  } catch {
    // Not a URL: no credential.
  }
  return { kind: "none" };
}
